import React, { forwardRef } from 'react';
import styles from './Toggle.module.css';

/**
 * Toggle/Switch Component
 *
 * A modern toggle switch component with smooth animations
 * following the AB Civil design system and 2025 UI standards.
 *
 * Perfect for binary on/off states (enabled/disabled, yes/no, etc.)
 *
 * @param {Object} props
 * @param {string} props.label - Toggle label
 * @param {boolean} props.checked - Checked/enabled state
 * @param {function} props.onChange - Change handler
 * @param {boolean} props.disabled - Disable toggle
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {string} props.size - Toggle size: 'sm' | 'md' | 'lg'
 * @param {string} props.labelPosition - Label position: 'left' | 'right'
 * @param {boolean} props.showLabels - Show ON/OFF labels on the toggle
 * @param {string} props.className - Additional CSS classes
 */
const Toggle = forwardRef(
  (
    {
      label = null,
      checked = false,
      onChange,
      disabled = false,
      error = null,
      helperText = null,
      size = 'md',
      labelPosition = 'left',
      showLabels = false,
      className = '',
      ...rest
    },
    ref
  ) => {
    const hasError = !!error;
    const showHelperText = error || helperText;

    const containerClasses = [
      styles.container,
      hasError && styles.hasError,
      disabled && styles.isDisabled,
      styles[`label-${labelPosition}`],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const toggleClasses = [
      styles.toggle,
      styles[size],
      checked && styles.checked,
      hasError && styles.error,
      showLabels && styles.hasLabels,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        <label className={styles.label}>
          {label && labelPosition === 'left' && (
            <span className={styles.labelText}>{label}</span>
          )}

          <div className={styles.toggleWrapper}>
            <input
              ref={ref}
              type="checkbox"
              className={styles.input}
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              role="switch"
              aria-checked={checked}
              {...rest}
            />
            <span className={toggleClasses}>
              <span className={styles.slider}>
                {showLabels && (
                  <span className={styles.toggleLabels}>
                    <span className={styles.onLabel}>ON</span>
                    <span className={styles.offLabel}>OFF</span>
                  </span>
                )}
              </span>
            </span>
          </div>

          {label && labelPosition === 'right' && (
            <span className={styles.labelText}>{label}</span>
          )}
        </label>

        {showHelperText && (
          <p className={hasError ? styles.errorText : styles.helperText}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';

export default Toggle;
